import React, { useState, useEffect, useRef, useMemo } from "react";
import Link from "next/link";
import gql from "graphql-tag";
import { useQuery } from "@apollo/react-hooks";
import { get, isNil } from "lodash";
import {
  getCategoryName,
  parseApiCategory,
  getCategoryId,
  getCategorySlug
} from "data/api/ApiCategory";
import styled from "styled-components";
import { parseApiStory } from "data/api/ApiStory";
import { getNumStories } from "data/api/ApiCategory";
import { ArrowForward } from "@material-ui/icons";
import { baseUrl, environmentVariables } from "services/shared/Environment";
import ContentLoadingSpinner from "components/shared/spinners/ContentLoadingSpinner";
import CallToActionButton from "components/shared/buttons/CallToActionButton";
import { StoryList } from "components/shared/StoryList";
import { Box } from "@chakra-ui/core";
import { request } from 'graphql-request'
import { CtaType, useRecordCtaClicked } from "hooks/analytics/useRecordCtaClicked";
import { useCategories, useCategoryStories } from "components/optimized/Homepage/HomepageStories/HomepageStories.hooks";
import { usePageProps } from "components/optimized/Homepage/Homepage.hooks";

const crosswordCategoryId = "1a8Czxb72TD7mwAGq5tUu2"

const categoriesQuery = gql`
  query categoriesQuery {
    categories {
      id
      name
      numStories
      slug
    }
  }
`;


const categoryStoriesQuery = gql`
  query categoryStoriesQuery($categoryId: ID!, $numStoriesLoaded: Int!) {
    categoryStories(first: $numStoriesLoaded, categoryId: $categoryId) {
      id
      title
      slug
      publicationDate
      author {
        id
        fullName
        slug
      }
      authorMultiple {
        id
        fullName
        slug
      }
      summary
      featuredImage {
        id
        url
      }
    }
  }
`;

export function getLatestStoriesQuery(numStoriesLoaded: number) {
  return `
      query {
        latestStories(first: ${numStoriesLoaded}) {
          id
          slug
          title
          categories {
            id
            name
          }
          publicationDate
          author {
            id
            fullName
            slug
          }
          authorMultiple {
            id 
            fullName
            slug
          }
          summary
          featuredImage {
            id
            url
          }
        }
      }
  `;
}

const latestStoriesQuery = gql`
  query latestStoriesQuery($numStoriesLoaded: Int!) {
    latestStories(first: $numStoriesLoaded) {
      id
      slug
      title
      categories {
        id
        name
      }
      publicationDate
      author {
        id
        fullName
        slug
      }
      authorMultiple {
        id 
        fullName
        slug
      }
      summary
      featuredImage {
        id
        url
      }
    }
  }
`;

function useLatestStories({ numStoriesLoaded }) {

  const { latestStories: initialLatestStories } = usePageProps()

  const [latestStories, setLatestStories] = useState(initialLatestStories ?? []);
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if(numStoriesLoaded > 4) {
      setLoading(true)
      request(
          `${baseUrl()}/api/graphql`,
          getLatestStoriesQuery(numStoriesLoaded)
        )
        .then((data) => {
          setLatestStories(data?.latestStories ?? []);
        })
        .catch((e) => {
          console.log(e);
        }).finally(() => {
          setLoading(false);
        })
    }
  }, [numStoriesLoaded])

  const stories = useMemo(() => latestStories
    .map((story) => parseApiStory(story))
    .filter(
      (story) =>
        !story.categories.some(
          (category) => category.id === crosswordCategoryId
        )
    ), [latestStories])
    
  return { loading, stories, error: null };
}

const CategoryContainer = styled.div.attrs({
  className: "w-full flex justify-center pt-10 sm:pt-8"
})`
  h1,
  h2,
  h3 {
    font-family: "Poppins";
  }

  h4,
  h5,
  h6,
  p {
    font-family: "Karma";
  }

  :first-child {
    padding-top: 0;
  }
`;

function CategoryContent({ children }) {
  return (
    <Box position="relative" w="100%" maxWidth="1000px">
      {children}
    </Box>
  );
}

function CategoryName({ children }) {
  return (
    <div className="ml-6 mb-2">
      <h2 className="text-3xl font-bold text-gray-900 m-0">{children}</h2>
    </div>
  );
}

function LoadMoreStoriesButton({ storiesLoading, onClick, moreStoriesToLoad }) {
  return (
    moreStoriesToLoad && (
      <>
        <div className="w-full flex justify-center">
          <CallToActionButton disabled={storiesLoading} onClick={onClick}>
            {storiesLoading ? "Loading..." : "Load More"}
          </CallToActionButton>
        </div>
      </>
    )
  );
}

function CategoryStories({ category }) {
  const [numStoriesLoaded, setNumStoriesLoaded] = useState(4);

  const { stories, loading: storiesLoading } = useCategoryStories({
    categoryId: getCategoryId(category),
    numStoriesLoaded
  });

  const categoryHasMoreStories = numStoriesLoaded < getNumStories(category);

  return (
    <Stories
      numStoriesLoaded={numStoriesLoaded}
      setNumStoriesLoaded={setNumStoriesLoaded}
      stories={stories}
      storiesLoading={storiesLoading}
      moreStoriesToLoad={categoryHasMoreStories}
    />
  );
}

function LatestStories() {
  const [numStoriesLoaded, setNumStoriesLoaded] = useState(4);
  const { stories, loading: storiesLoading } = useLatestStories({
    numStoriesLoaded
  });

  return (
    <CategoryContainer>
      <CategoryContent>
        <CategoryName>Latest Stories</CategoryName>
        <Stories
          numStoriesLoaded={numStoriesLoaded}
          setNumStoriesLoaded={setNumStoriesLoaded}
          stories={stories}
          storiesLoading={storiesLoading}
          moreStoriesToLoad={true}
        />
      </CategoryContent>
    </CategoryContainer>
  );
}

function Stories({
  numStoriesLoaded,
  setNumStoriesLoaded,
  stories,
  storiesLoading,
  moreStoriesToLoad
}) {
  return (
    <>
      <StoryList stories={stories} />
      <LoadMoreStoriesButton
        onClick={() => setNumStoriesLoaded(numStoriesLoaded + 4)}
        storiesLoading={storiesLoading}
        moreStoriesToLoad={moreStoriesToLoad}
      />
    </>
  );
}

const SeeAllArrow = styled<any>(ArrowForward)`
  color: ${props => props.theme.colors.orange};
`;

function SeeAllLink({ category }) {

  const recordCtaClicked = useRecordCtaClicked()

  return (
    <div className="flex items-center" onClick={() => {
      recordCtaClicked({
        ctaText: "See all stories",
        ctaDestinationUrl: `${baseUrl()}/category/${getCategorySlug(category)}`,
        ctaType: CtaType.button,
      });
    }}>
      <Link href={`/category/${getCategorySlug(category)}`}>
        <a className="ml-6 text-lg font-medium cursor-pointer underline orange">
          {" "}
          See all stories{" "}
        </a>
      </Link>
      <SeeAllArrow />
    </div>
  );
}

function Category({ category }) {
  return (
    <CategoryContainer>
      <CategoryContent>
        <CategoryName>{getCategoryName(category)}</CategoryName>
        <SeeAllLink category={category} />
        <CategoryStories category={category} />
      </CategoryContent>
    </CategoryContainer>
  );
}

export default () => {
  const { categories: pageCategories } = useCategories();

  const categories = pageCategories ?? []

  const [editorsPicks] = categories.filter(
    category =>
      getCategoryId(category) ===
      environmentVariables().EDITORS_PICKS_CATEGORY_ID
  );

  const categoriesWithoutEditorsPicks = categories.filter(
    category =>
      getCategoryId(category) !==
      environmentVariables().EDITORS_PICKS_CATEGORY_ID
  );

  return (
    <div className="w-full py-10">
      <LatestStories />
      <Category category={editorsPicks} />
      {categoriesWithoutEditorsPicks.map(category => (
        <Category category={category} key={getCategoryId(category)} />
      ))}
    </div>
  );
};
